import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/router";
import useDarkMode from "hooks/useDarkMode";
import { MdNavigateBefore, MdOutlineNavigateNext } from "react-icons/md";

const defaultTopBubbles = [
  { id: "1", name: "Բիլ Գեյթս", image: "https://i.pravatar.cc/300?img=33", points: "60%" },
  { id: "2", name: "Անի Էրեմյան", image: "https://i.pravatar.cc/300?img=47", points: "90%" },
  { id: "3", name: "Հովհիկ Աղազարյան", image: "https://i.pravatar.cc/300?img=12", points: "85%" },
  { id: "4", name: "Արամ Սարգսյան", image: "https://i.pravatar.cc/300?img=60", points: "72%" },
];

export default function HeroSection({ bubbles = [] }) {
  const router = useRouter();
  const { theme, mounted } = useDarkMode();
  const isLight = mounted && theme === "light";
  const predictorList = bubbles && bubbles.length > 0 ? bubbles : defaultTopBubbles;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (predictorList.length <= 1) return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % predictorList.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [predictorList.length]);

  const activePredictor = predictorList[activeIndex] || predictorList[0];

  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + predictorList.length) % predictorList.length);
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % predictorList.length);
  };

  const nodePositions = [
    { top: "10%", left: "30%", size: "w-11 h-11" },
    { top: "28%", left: "60%", size: "w-14 h-14" },
    { top: "48%", left: "20%", size: "w-12 h-12" },
    { top: "65%", left: "55%", size: "w-14 h-14" },
    { top: "82%", left: "35%", size: "w-11 h-11" },
  ];

  return (
    <section
      style={{ fontFamily: "Noto Sans Armenian" }}
      className={`relative w-full pt-6 pb-10 sm:pt-8 sm:pb-12 lg:py-16 px-4 sm:px-8 md:px-12 lg:px-20 overflow-hidden ${
        isLight ? "bg-white" : "bg-[#030014]"
      }`}
    >
      {/* Background SVG Wave Chart */}
      <div className="absolute inset-0 pointer-events-none z-0 opacity-40 md:opacity-60 overflow-hidden">
        <svg
          className="w-full h-full"
          viewBox="0 0 1200 600"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M 50 150 L 1150 150 M 50 300 L 1150 300 M 50 450 L 1150 450"
            stroke={isLight ? "#f1f5f9" : "#1e293b"}
            strokeDasharray="4 4"
            strokeWidth="1"
          />
          <path
            d="M 50 500 Q 250 480 400 350 T 700 200 T 950 120 T 1150 80"
            stroke={isLight ? "#e0e7ff" : "#1e3a8a"}
            strokeWidth="2"
            fill="none"
          />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        {/* Left Column: Heading & Controls */}
        <div className="lg:col-span-6 flex flex-col justify-between min-h-0 lg:min-h-[340px]">
          <div>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-black text-[#0033FF] leading-[1.25] tracking-tight max-w-xl break-words">
              Կանխատեսումները չեն մնում խոսքի մակարդակում
            </h1>
            <p className="text-slate-600 dark:text-slate-300 text-sm sm:text-base md:text-lg font-normal leading-relaxed mt-4 sm:mt-5 max-w-lg">
              Համակարգը հետևում է դրանց իրականացմանը և փոփոխում հեղինակների վարկանիշները:
            </p>
          </div>

          {/* Controls & Pagination Dots */}
          <div className="flex items-center gap-3 sm:gap-4 mt-6 sm:mt-8">
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handlePrev}
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#f0f4ff] dark:bg-slate-800 text-[#0033FF] hover:bg-[#0033FF] hover:text-white flex items-center justify-center transition-all cursor-pointer shadow-sm"
                aria-label="Նախորդ"
              >
                <MdNavigateBefore size={24} />
              </button>
              <button
                onClick={handleNext}
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#f0f4ff] dark:bg-slate-800 text-[#0033FF] hover:bg-[#0033FF] hover:text-white flex items-center justify-center transition-all cursor-pointer shadow-sm"
                aria-label="Հաջորդ"
              >
                <MdOutlineNavigateNext size={24} />
              </button>
            </div>

            {/* Pagination Dots (Clamped to max 6 to prevent horizontal overflow on mobile) */}
            <div className="flex items-center gap-1.5 ml-2 overflow-hidden shrink-0">
              {predictorList.slice(0, Math.min(predictorList.length, 6)).map((_, i) => {
                const isCurrent =
                  activeIndex === i ||
                  (activeIndex >= 6 && i === 5);
                return (
                  <button
                    key={i}
                    onClick={() => setActiveIndex(i)}
                    className={`h-2 rounded-full transition-all ${
                      isCurrent
                        ? "w-6 bg-[#0033FF]"
                        : "w-2 bg-slate-200 dark:bg-slate-700"
                    }`}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Center Floating Nodes (Desktop Only) */}
        <div className="hidden lg:block lg:col-span-2 relative h-[360px]">
          {predictorList.map((p, idx) => {
            const pos = nodePositions[idx % nodePositions.length];
            const isActiveNode = activeIndex === idx;
            const name = p.name || `${p.firstName || ''} ${p.lastName || ''}`;

            return (
              <motion.div
                key={p.id || idx}
                onClick={() => setActiveIndex(idx)}
                className={`group absolute rounded-full p-0.5 cursor-pointer transition-all duration-300 ${pos.size} ${
                  isActiveNode
                    ? "ring-4 ring-[#0033FF]/30 scale-125 z-20 shadow-md"
                    : "opacity-80 hover:opacity-100 hover:scale-110 z-10"
                }`}
                style={{ top: pos.top, left: pos.left }}
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3.5 + idx * 0.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className={`w-full h-full rounded-full overflow-hidden border-2 ${
                  isActiveNode ? "border-[#0033FF]" : "border-white dark:border-slate-800"
                }`}>
                  <img
                    src={p.image || "https://i.pravatar.cc/300"}
                    alt={name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Minimalist Hover Tooltip Name */}
                <div className="absolute left-1/2 -translate-x-1/2 -bottom-7 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 bg-slate-900/90 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap shadow-sm">
                  {name}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Right Column: Featured Forecaster Card */}
        <div className="lg:col-span-4 flex justify-center lg:justify-end">
          <AnimatePresence mode="wait">
            <motion.div
              key={activePredictor.id || activeIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              onClick={() => activePredictor.id && router.push(`/predictors/${activePredictor.id}`)}
              className="bg-white dark:bg-[#0c0a1f] rounded-[32px] p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-slate-100 dark:border-white/10 flex flex-col items-center justify-between text-center w-full max-w-[300px] min-h-[360px] cursor-pointer hover:shadow-md transition-all"
            >
              {/* Forecaster Avatar */}
              <div className="relative mt-2">
                <div className="w-32 h-32 rounded-full border-4 border-[#0033FF] overflow-hidden shadow-sm">
                  <img
                    src={activePredictor.image || "https://i.pravatar.cc/300"}
                    alt={activePredictor.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Forecaster Name */}
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-3">
                {activePredictor.name || `${activePredictor.firstName || ''} ${activePredictor.lastName || ''}`}
              </h3>

              {/* Accuracy Badge Box */}
              <div className="bg-[#f0f4ff] dark:bg-slate-900/80 rounded-2xl p-4 w-full mt-4 flex flex-col items-center justify-center">
                <span className="text-3xl font-black text-[#0033FF]">
                  {typeof activePredictor.collectedPoints === "number"
                    ? `${activePredictor.collectedPoints}%`
                    : activePredictor.points || "60%"}
                </span>
                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 mt-1 uppercase tracking-wider">
                  Կանխատեսումներ
                </span>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
